import { useState } from 'react';
import Button from '@/components/base/Button';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { Pill } from '@/components/base/Badges';
import type { PillTone } from '@/components/base/Badges';
import { settlementDues, settlementsHistory } from '@/mocks/finance';

type SettlementStatus = 'transferred' | 'processing' | 'rejected';

const statusConfig: Record<SettlementStatus, { label: string; tone: PillTone }> = {
  transferred: { label: 'تم التحويل', tone: 'successSolid' },
  processing: { label: 'قيد المعالجة', tone: 'warning' },
  rejected: { label: 'مرفوض', tone: 'danger' },
};

interface Settlement {
  id: string;
  requestDate: string;
  processDate: string;
  amount: number;
  status: string;
  note: string;
}

export default function SettlementsTab() {
  const [confirm, setConfirm] = useState(false);
  const [hasPending] = useState(false);
  const [settlements, setSettlements] = useState<Settlement[]>(settlementsHistory);

  const requestSettlement = () => {
    const newSettlement: Settlement = {
      id: `ST-${Date.now()}`,
      requestDate: '2026-09-16',
      processDate: '—',
      amount: settlementDues,
      status: 'processing',
      note: '',
    };
    setSettlements((prev) => [newSettlement, ...prev]);
    setConfirm(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 rounded-card border border-background-200 bg-white p-5 shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[13px] text-foreground-500">المبالغ المستحقة</p>
          <p className="mt-1 font-bold text-[28px] leading-tight text-primary-600">
            {settlementDues.toLocaleString('ar-SA')}{' '}
            <span className="text-[16px] font-medium text-foreground-400">ر.س</span>
          </p>
          <p className="mt-1 text-[12px] text-foreground-400">
            بعد خصم العمولات ورسوم التوصيل
          </p>
        </div>
        {hasPending ? (
          <div className="rounded-card bg-warning-50 px-4 py-3">
            <p className="text-[13px] font-medium text-warning-700">
              لديك طلب تسوية قيد المعالجة — سيتم التحويل قريباً
            </p>
          </div>
        ) : (
          <Button
            variant="cta"
            size="lg"
            icon="ri-bank-line"
            onClick={() => setConfirm(true)}
          >
            طلب تسوية
          </Button>
        )}
      </div>

      <h3 className="font-semibold text-[15px] text-foreground-950">سجل التسويات</h3>
      <div className="overflow-hidden rounded-card border border-background-200 bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] border-collapse text-right">
            <thead>
              <tr className="bg-background-50">
                {['تاريخ الطلب', 'تاريخ المعالجة', 'المبلغ', 'الحالة'].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold text-[12px] text-foreground-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {settlements.map((settlement, index) => {
                const config = statusConfig[settlement.status as SettlementStatus] || statusConfig.processing;
                return (
                  <tr key={settlement.id} className={index > 0 ? 'border-t border-background-100' : ''}>
                    <td className="px-4 py-3.5 text-[13px] text-foreground-700">{settlement.requestDate}</td>
                    <td className="px-4 py-3.5 text-[12px] text-foreground-500">{settlement.processDate}</td>
                    <td className="px-4 py-3.5 font-semibold text-[13px] text-foreground-950">
                      {settlement.amount.toLocaleString('ar-SA')} ر.س
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <Pill tone={config.tone}>{config.label}</Pill>
                        {settlement.status === 'rejected' && settlement.note && (
                          <div className="group relative">
                            <i className="ri-error-warning-line cursor-help text-danger-500" aria-hidden="true"></i>
                            <div className="invisible absolute bottom-full right-0 z-10 mb-1 w-[200px] rounded-card border border-background-200 bg-white p-2 text-[12px] text-foreground-700 shadow-soft group-hover:visible">
                              {settlement.note}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirm}
        title="تأكيد طلب التسوية"
        message={
          <div>
            <p>سيتم طلب تحويل المبلغ المستحق:</p>
            <p className="mt-2 font-bold text-[20px] text-primary-600">
              {settlementDues.toLocaleString('ar-SA')} ر.س
            </p>
            <p className="mt-3 text-[12px] text-foreground-400">
              سيتم تحويل المبلغ بعد مراجعة الإدارة وموافقتها، عادةً خلال 2-3 أيام عمل.
            </p>
          </div>
        }
        confirmLabel="تأكيد الطلب"
        tone="primary"
        onConfirm={requestSettlement}
        onClose={() => setConfirm(false)}
      />
    </div>
  );
}